import type { Rpc } from "@effect/rpc";
import type {
	DefaultFunctionArgs,
	RegisteredMutation,
	RegisteredQuery,
	RegisteredAction,
	GenericQueryCtx,
	GenericMutationCtx,
	GenericActionCtx,
	GenericDataModel,
} from "convex/server";
import {
	queryGeneric,
	internalQueryGeneric,
	internalMutationGeneric,
	internalActionGeneric,
} from "convex/server";
import { v } from "convex/values";
import * as Cause from "effect/Cause";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import {
	ConfectActionCtx,
	ConfectMutationCtx,
	ConfectQueryCtx,
	makeQueryCtx,
	makeMutationCtx,
	makeActionCtx,
	type GenericConfectSchema,
	type TableNamesInSchema,
	type DocumentFromTable,
	type EncodedDocumentFromTable,
} from "../ctx";
import type { ConfectSchemaDefinition } from "../schema";

type ConvexCtx = GenericQueryCtx<GenericDataModel> | GenericMutationCtx<GenericDataModel> | GenericActionCtx<GenericDataModel>;

type TableSchemas<Tables extends GenericConfectSchema> = {
	[TableName in TableNamesInSchema<Tables>]: Schema.Schema<
		DocumentFromTable<Tables, TableName>,
		EncodedDocumentFromTable<Tables, TableName>
	>;
};

const extractTableSchemas = <Tables extends GenericConfectSchema>(
	tables: Tables,
): TableSchemas<Tables> => {
	const result: Record<string, Schema.Schema.AnyNoContext> = {};
	for (const [tableName, tableDef] of Object.entries(tables)) {
		result[tableName] = (tableDef as { documentSchema: Schema.Schema.AnyNoContext }).documentSchema;
	}
	return result as TableSchemas<Tables>;
};

export interface RpcEndpoint<
	Tag extends string,
	R extends Rpc.Any,
	ConvexFn,
> {
	readonly _tag: Tag;
	/** Phantom type carrier — never constructed at runtime; client typing extracts payload/success/error from `R`. */
	readonly rpc: R;
	readonly fn: ConvexFn;
}

// ---------------------------------------------------------------------------
// Handler ref — mutable slot filled by .implement()
// ---------------------------------------------------------------------------

type HandlerFn = (payload: never) => Effect.Effect<unknown, unknown, unknown>;

interface HandlerRef {
	current: HandlerFn | null;
	tag: string | null;
}

const createHandlerRef = (): HandlerRef => ({ current: null, tag: null });

// ---------------------------------------------------------------------------
// UnbuiltRpcEndpoint — definition only, no handler body
// ---------------------------------------------------------------------------

/**
 * An endpoint whose schema has been defined but whose handler may or may not
 * be wired yet. Call `.implement(handler)` to provide the implementation.
 *
 * The handler is stored in a mutable ref so that the definition file (which
 * `api.d.ts` imports) never needs to contain handler code. The implementation
 * can live in a separate file, breaking circular type dependencies.
 */
export interface UnbuiltRpcEndpoint<
	PayloadFields extends Schema.Struct.Fields,
	Success extends Schema.Schema.AnyNoContext,
	Error extends Schema.Schema.AnyNoContext | undefined,
	ConvexFnType,
> {
	readonly __unbuilt: true;
	readonly kind: string;
	readonly payloadFields: PayloadFields;
	readonly successSchema: Success;
	readonly errorSchema: Error | undefined;
	/**
	 * Phantom type carrier (the runtime value is always `undefined`). Lets
	 * `EndpointFn` extract the flat registered-function type with a shallow
	 * structural match, without instantiating the Rpc/Schema tower — see
	 * `EndpointFn`. Required (not optional) so the extraction needs no
	 * `NonNullable` wrapper, which Convex's `FilterApi` cannot infer through.
	 */
	readonly _convexFnType: ConvexFnType;
	readonly build: (tag: string) => RpcEndpoint<string, Rpc.Any, ConvexFnType>;

	/**
	 * Wire the handler implementation for this endpoint.
	 *
	 * Can be called from a separate file to avoid circular type dependencies
	 * with Convex's generated `api.d.ts`.
	 */
	implement(
		handler: (
			payload: Schema.Struct.Type<PayloadFields>,
		) => Effect.Effect<
			Schema.Schema.Type<Success>,
			Error extends Schema.Schema.AnyNoContext ? Schema.Schema.Type<Error> : never,
			unknown
		>,
	): void;
}

export interface RpcFactoryConfig<ConfectSchema extends GenericConfectSchema> {
	readonly schema: ConfectSchemaDefinition<ConfectSchema>;
}

/**
 * Flat wire envelope for Confect-registered functions.
 *
 * Deliberately NOT Effect's `ExitEncoded`: that type embeds the recursive
 * `CauseEncoded` tree (Sequential/Parallel/Die/Interrupt), which both (a)
 * forces every API consumer to understand Effect's Cause JSON and (b) is deep
 * enough at the type level to trip TS2589 ("instantiation excessively deep")
 * when combined with Convex's generated `ApiFromModules` types.
 *
 * Failures carry the typed domain error directly; everything else (defects,
 * interrupts) is squashed server-side into a pretty-printed `Defect` message.
 * Named interfaces (not inline type expressions) so TypeScript caches them by
 * identity at every consumer.
 */
export interface RpcSuccessEncoded<A = unknown> {
	readonly _tag: "Success";
	readonly value: A;
}

export interface RpcFailureEncoded<E = unknown> {
	readonly _tag: "Failure";
	readonly error: E;
}

export interface RpcDefectEncoded {
	/**
	 * Defects are serialized with `Schema.Defect` — Errors become
	 * `{name, message}` (never stack traces), strings stay strings, other
	 * values become JSON strings.
	 */
	readonly _tag: "Defect";
	readonly defect: unknown;
}

export type RpcResultEncoded<A = unknown, E = unknown> =
	| RpcSuccessEncoded<A>
	| RpcFailureEncoded<E>
	| RpcDefectEncoded;

type EndpointKind = "query" | "internalQuery" | "internalMutation" | "internalAction";

type ConvexRegistrar = (opts: { args: ReturnType<typeof v.any>; handler: (ctx: ConvexCtx, args: unknown) => Promise<RpcResultEncoded> }) => unknown;

interface EndpointConfig<Ctx, ConvexFnType> {
	readonly kind: EndpointKind;
	readonly ctxTag: Context.Tag<Ctx, Ctx>;
	readonly makeCtx: (rawCtx: ConvexCtx) => Ctx;
	readonly registrar: ConvexRegistrar;
	readonly _convexFnType?: ConvexFnType;
}

export const createRpcFactory = <ConfectSchema extends GenericConfectSchema>(
	config: RpcFactoryConfig<ConfectSchema>,
) => {
	const tableSchemas = extractTableSchemas(config.schema.tables);

	const makeConvexHandler = <
		PayloadFields extends Schema.Struct.Fields,
		Success extends Schema.Schema.AnyNoContext,
		Error extends Schema.Schema.AnyNoContext | undefined,
		Ctx,
	>(
		endpointConfig: EndpointConfig<Ctx, unknown>,
		tag: string,
		payloadFields: PayloadFields,
		successSchema: Success,
		errorSchema: Error,
		handlerRef: HandlerRef,
	) => {
		const payloadSchema = Schema.Struct(payloadFields) as unknown as Schema.Schema<Schema.Struct.Type<PayloadFields>, Schema.Struct.Encoded<PayloadFields>, never>;
		const decodePayload = Schema.decodeUnknownSync(payloadSchema);

		const resultSchema = Schema.Union(
			Schema.TaggedStruct("Success", {
				value: successSchema as Schema.Schema<unknown, unknown, never>,
			}),
			Schema.TaggedStruct("Failure", {
				error: (errorSchema ?? Schema.Never) as Schema.Schema<unknown, unknown, never>,
			}),
			// Schema.Defect serializes Errors as {name, message} (no stack traces),
			// strings as-is, and other values as JSON strings.
			Schema.TaggedStruct("Defect", { defect: Schema.Defect }),
		);
		const encodeResult = Schema.encodeSync(resultSchema) as (
			result: RpcResultEncoded,
		) => RpcResultEncoded;

		const encodeDefect = (defect: unknown): RpcResultEncoded =>
			encodeResult({ _tag: "Defect", defect });

		const encodeExit = (exit: Exit.Exit<unknown, unknown>): RpcResultEncoded =>
			Exit.match(exit, {
				onSuccess: (value) => encodeResult({ _tag: "Success", value }),
				onFailure: (cause) =>
					Option.match(Cause.failureOption(cause), {
						onSome: (error) => {
							// Errors not declared in the endpoint's error schema are
							// defects by contract: encode them safely instead of failing
							// the whole response.
							try {
								return encodeResult({ _tag: "Failure", error });
							} catch {
								return encodeDefect(error);
							}
						},
						// No typed failure → squash the cause (defect, interrupt, …)
						// into its primary value and serialize it safely.
						onNone: () => encodeDefect(Cause.squash(cause)),
					}),
			});

		return async (rawCtx: ConvexCtx, args: unknown): Promise<RpcResultEncoded> => {
			if (!handlerRef.current) {
				throw new Error(
					`Handler not implemented for "${tag}". Call .implement() on the endpoint definition.`,
				);
			}
			const handler = handlerRef.current;

			let decodedArgs: Schema.Struct.Type<PayloadFields>;
			try {
				decodedArgs = decodePayload(args);
			} catch (err) {
				return encodeExit(Exit.die(err));
			}

			const effect = Effect.provideService(
				handler(decodedArgs as never) as Effect.Effect<Schema.Schema.Type<Success>, unknown, Ctx>,
				endpointConfig.ctxTag,
				endpointConfig.makeCtx(rawCtx),
			);

			const exit = await Effect.runPromiseExit(effect);
			return encodeExit(exit);
		};
	};

	const createUnbuiltEndpoint = <
		PayloadFields extends Schema.Struct.Fields,
		Success extends Schema.Schema.AnyNoContext,
		Error extends Schema.Schema.AnyNoContext | undefined,
		ConvexFnType,
		Ctx,
	>(
		endpointConfig: EndpointConfig<Ctx, ConvexFnType>,
		payloadFields: PayloadFields,
		successSchema: Success,
		errorSchema: Error | undefined,
		handlerRef: HandlerRef,
	): UnbuiltRpcEndpoint<PayloadFields, Success, Error, ConvexFnType> => ({
		__unbuilt: true as const,
		kind: endpointConfig.kind,
		payloadFields,
		successSchema: successSchema,
		errorSchema: errorSchema,
		// Phantom only — see the interface doc. `never` is assignable to any
		// ConvexFnType; the value is never read.
		_convexFnType: undefined as never,
		build: (tag): RpcEndpoint<string, Rpc.Any, ConvexFnType> => {
			handlerRef.tag = tag;
			const convexHandler = makeConvexHandler(
				endpointConfig,
				tag,
				payloadFields,
				successSchema,
				errorSchema,
				handlerRef,
			);
			const fn = endpointConfig.registrar({ args: v.any(), handler: convexHandler });
			// `rpc` is a phantom type carrier; no @effect/rpc value is constructed.
			return { _tag: tag, rpc: undefined as never, fn: fn as ConvexFnType };
		},
		implement(
			handler: (
				payload: Schema.Struct.Type<PayloadFields>,
			) => Effect.Effect<
				Schema.Schema.Type<Success>,
				Error extends Schema.Schema.AnyNoContext ? Schema.Schema.Type<Error> : never,
				unknown
			>,
		): void {
			handlerRef.current = handler as HandlerFn;
		},
	});

	/**
	 * Create an endpoint definition method. The returned function takes only
	 * schemas (no handler). Call `.implement(handler)` on the result to wire
	 * the handler, potentially from a separate file.
	 */
	const createEndpointMethod = <Ctx, ConvexFnType>(
		endpointConfig: EndpointConfig<Ctx, ConvexFnType>,
	) => <
		PayloadFields extends Schema.Struct.Fields = {},
		Success extends Schema.Schema.AnyNoContext = typeof Schema.Void,
		Error extends Schema.Schema.AnyNoContext | undefined = undefined,
	>(
		options: {
			readonly payload?: PayloadFields;
			readonly success: Success;
			readonly error?: Error;
		},
	): UnbuiltRpcEndpoint<PayloadFields, Success, Error, ConvexFnType> => {
		const handlerRef = createHandlerRef();
		return createUnbuiltEndpoint(
			endpointConfig,
			(options.payload ?? {}) as PayloadFields,
			options.success,
			options.error,
			handlerRef,
		);
	};

	const queryConfig: EndpointConfig<ConfectQueryCtx<ConfectSchema>, RegisteredQuery<"public", DefaultFunctionArgs, Promise<RpcResultEncoded>>> = {
		kind: "query",
		ctxTag: ConfectQueryCtx<ConfectSchema>(),
		makeCtx: (ctx) => makeQueryCtx(ctx as GenericQueryCtx<GenericDataModel>, tableSchemas),
		registrar: queryGeneric,
	};

	const internalQueryConfig: EndpointConfig<ConfectQueryCtx<ConfectSchema>, RegisteredQuery<"internal", DefaultFunctionArgs, Promise<RpcResultEncoded>>> = {
		kind: "internalQuery",
		ctxTag: ConfectQueryCtx<ConfectSchema>(),
		makeCtx: (ctx) => makeQueryCtx(ctx as GenericQueryCtx<GenericDataModel>, tableSchemas),
		registrar: internalQueryGeneric,
	};

	const internalMutationConfig: EndpointConfig<ConfectMutationCtx<ConfectSchema>, RegisteredMutation<"internal", DefaultFunctionArgs, Promise<RpcResultEncoded>>> = {
		kind: "internalMutation",
		ctxTag: ConfectMutationCtx<ConfectSchema>(),
		makeCtx: (ctx) => makeMutationCtx(ctx as GenericMutationCtx<GenericDataModel>, tableSchemas),
		registrar: internalMutationGeneric,
	};

	const internalActionConfig: EndpointConfig<ConfectActionCtx<ConfectSchema>, RegisteredAction<"internal", DefaultFunctionArgs, Promise<RpcResultEncoded>>> = {
		kind: "internalAction",
		ctxTag: ConfectActionCtx<ConfectSchema>(),
		makeCtx: (ctx) => makeActionCtx(ctx as GenericActionCtx<GenericDataModel>),
		registrar: internalActionGeneric,
	};

	return {
		query: createEndpointMethod(queryConfig),
		internalQuery: createEndpointMethod(internalQueryConfig),
		internalMutation: createEndpointMethod(internalMutationConfig),
		internalAction: createEndpointMethod(internalActionConfig),
	};
};

/**
 * Extract the registered Convex function type from an unbuilt endpoint via
 * the `_convexFnType` phantom. This is deliberately a single-property
 * structural match: extracting through `RpcEndpoint`/`Rpc.Rpc` would force
 * TypeScript to instantiate the full Schema tower for every endpoint, which
 * is what used to push the generated `api` types over the TS2589
 * instantiation-depth limit. Handler exports stay flat
 * (`Registered* <visibility, DefaultFunctionArgs, Promise<RpcResultEncoded>>`).
 */
export type EndpointFn<U> = U extends { readonly _convexFnType: infer Fn }
	? Fn
	: never;

export interface AnyRpcModule {
	readonly handlers: Record<string, unknown>;
}

interface AnyUnbuiltEndpoint {
	readonly __unbuilt: true;
	readonly kind: string;
	readonly payloadFields: Schema.Struct.Fields;
	readonly successSchema: Schema.Schema.AnyNoContext;
	readonly errorSchema: Schema.Schema.AnyNoContext | undefined;
	readonly build: (tag: string) => RpcEndpoint<string, Rpc.Any, unknown>;
	// Variance note: `implement` sits in covariant position (method), its handler
	// parameter is contravariant, and the handler's `payload` is again contravariant
	// — making `payload` doubly contravariant = covariant overall. So for
	// `AnyUnbuiltEndpoint` to be a proper supertype of all concrete endpoints,
	// `payload` must be a supertype of all concrete payload types → `unknown`.
	implement(handler: (payload: unknown) => Effect.Effect<unknown, unknown, unknown>): void;
}

type BuiltEndpoint<K extends string, U> = U extends UnbuiltRpcEndpoint<
	infer PayloadFields,
	infer Success,
	infer Error,
	infer ConvexFnType
>
	? RpcEndpoint<K, Rpc.Rpc<K, Schema.Struct<PayloadFields>, Success, Error extends Schema.Schema.AnyNoContext ? Error : typeof Schema.Never, never>, ConvexFnType>
	: never;

/**
 * Shallow constraint for makeRpcModule's input record.
 *
 * Deliberately NOT `AnyUnbuiltEndpoint`: checking each concrete endpoint def
 * against that interface forces TypeScript to relate their `implement` method
 * signatures, which instantiates the full Schema tower of every endpoint —
 * the single hottest path in compile traces and the main contributor to
 * TS2589 at api consumers. The brand keeps the call-site check O(1) per
 * endpoint; `isUnbuilt` validates the real shape at runtime.
 */
export interface UnbuiltEndpointBrand {
	readonly __unbuilt: true;
}

const isUnbuilt = (value: unknown): value is AnyUnbuiltEndpoint =>
	typeof value === "object" && value !== null && "__unbuilt" in value && (value as { __unbuilt: unknown }).__unbuilt === true;

export function makeRpcModule<
	const T extends Record<string, UnbuiltEndpointBrand>,
>(
	unbuiltEndpoints: T,
): { readonly handlers: { readonly [K in keyof T]: EndpointFn<T[K]> } } & {
	readonly [K in keyof T]: BuiltEndpoint<K & string, T[K]>;
} {
	const handlers = {} as Record<string, unknown>;
	const builtEndpoints = {} as Record<string, RpcEndpoint<string, Rpc.Any, unknown>>;

	// Runtime shape validation replaces the deep compile-time constraint —
	// see UnbuiltEndpointBrand.
	const endpointRecord = unbuiltEndpoints as unknown as Record<string, AnyUnbuiltEndpoint>;
	for (const key of Object.keys(endpointRecord)) {
		const unbuilt = endpointRecord[key]!;
		if (!isUnbuilt(unbuilt)) {
			throw new Error(`Expected unbuilt endpoint for key "${key}"`);
		}
		const endpoint = unbuilt.build(key);
		builtEndpoints[key] = endpoint;
		handlers[key] = endpoint.fn;
	}

	const module = {
		handlers: handlers as { readonly [K in keyof T]: EndpointFn<T[K]> },
	};

	return Object.assign(module, builtEndpoints) as {
		readonly handlers: { readonly [K in keyof T]: EndpointFn<T[K]> };
	} & { readonly [K in keyof T]: BuiltEndpoint<K & string, T[K]> };
}
